import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import type { CT600CompanyInfoForm } from '../../types/ct600Filing'

interface CollapsibleProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}

function Section({ title, children, defaultOpen = false }: CollapsibleProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  return (
    <div className='rounded-lg border border-gray-200 bg-white'>
      <button
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        className='flex w-full items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors'
      >
        <h3 className='text-base font-semibold text-gray-900'>{title}</h3>
        {isOpen ? <ChevronUp className='h-5 w-5 text-gray-500' /> : <ChevronDown className='h-5 w-5 text-gray-500' />}
      </button>
      {isOpen && <div className='border-t border-gray-100 px-6 pb-6 pt-4'>{children}</div>}
    </div>
  )
}

interface Props {
  companyInfo: CT600CompanyInfoForm
  onCompanyInfoChange: (update: CT600CompanyInfoForm) => void
  validationErrors: Record<string, string>
}

export default function CT600StepCompanyInfo({ companyInfo, onCompanyInfoChange, validationErrors }: Props) {
  const updateDetails = (field: string, value: unknown) => {
    onCompanyInfoChange({
      ...companyInfo,
      details: { ...companyInfo.details, [field]: value },
    })
  }

  const updateContact = (field: string, value: string) => {
    onCompanyInfoChange({
      ...companyInfo,
      contact: { ...companyInfo.contact, [field]: value },
    })
  }

  const updateOffice = (field: string, value: string) => {
    onCompanyInfoChange({
      ...companyInfo,
      registeredOffice: { ...companyInfo.registeredOffice, [field]: value },
    })
  }

  const updateIxbrl = (field: string, value: string) => {
    onCompanyInfoChange({
      ...companyInfo,
      ixbrlFiling: { ...companyInfo.ixbrlFiling, [field]: value },
    })
  }

  const handleEmployeesChange = (raw: string) => {
    // Only allow digits (no decimals, no negatives) for headcount.
    const digits = raw.replace(/[^\d]/g, '')
    updateIxbrl('averageEmployees', digits)
  }

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-xl font-bold text-gray-900'>Company Information</h2>
        <p className='mt-1 text-sm text-gray-600'>
          Review and confirm your company details for this return.
        </p>
      </div>

      <Section title='Return Details' defaultOpen>
        <div className='space-y-4'>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-900'>Company Name</label>
              <input
                type='text'
                value={companyInfo.details.companyName}
                onChange={(e) => updateDetails('companyName', e.target.value)}
                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
              />
            </div>
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-900'>Company Registration Number</label>
              <input
                type='text'
                value={companyInfo.details.companyRegistrationNumber}
                readOnly
                className='w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-600'
              />
            </div>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-900'>UTR</label>
              <input
                type='text'
                value={companyInfo.details.utr}
                readOnly
                className='w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-600'
              />
            </div>
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-900'>Trading Name</label>
              <input
                type='text'
                value={companyInfo.details.tradingName}
                onChange={(e) => updateDetails('tradingName', e.target.value)}
                placeholder='Same as company name if not different'
                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
              />
            </div>
          </div>

          {/* <div className='flex flex-wrap gap-6 pt-2'>
            {[
              { key: 'firstReturn', label: 'First return' },
              { key: 'finalReturn', label: 'Final return' },
              { key: 'amendedReturn', label: 'Amended return' },
            ].map(({ key, label }) => (
              <label key={key} className='flex items-center gap-2 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={companyInfo.details[key as keyof typeof companyInfo.details] as boolean}
                  onChange={(e) => updateDetails(key, e.target.checked)}
                  className='h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900'
                />
                <span className='text-sm text-gray-700'>{label}</span>
              </label>
            ))}
          </div> */}
        </div>
      </Section>

      <Section title='iXBRL Filing'>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-900'>
              Average Number of Employees
            </label>
            <input
              type='text'
              inputMode='numeric'
              pattern='[0-9]*'
              value={companyInfo.ixbrlFiling.averageEmployees}
              onChange={e => handleEmployeesChange(e.target.value)}
              placeholder='e.g. 3'
              className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
            />
            <p className='mt-1 text-xs text-gray-500'>
              Average employee headcount for the accounting period — included
              in your iXBRL accounts.
            </p>
          </div>
        </div>
      </Section>

      <Section title='Contact Person'>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-900'>
              Full Name <span className='text-red-600'>*</span>
            </label>
            <input
              type='text'
              required
              value={companyInfo.contact.name}
              onChange={(e) => updateContact('name', e.target.value)}
              className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
            />
            {validationErrors.contactName && (
              <p className='mt-1 text-xs text-red-600'>{validationErrors.contactName}</p>
            )}
          </div>
          <div>
            <label className='mb-2 block text-sm font-medium text-gray-900'>
              Phone <span className='text-red-600'>*</span>
            </label>
            <input
              type='tel'
              required
              value={companyInfo.contact.phone}
              onChange={(e) => updateContact('phone', e.target.value)}
              className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
            />
            {validationErrors.contactPhone && (
              <p className='mt-1 text-xs text-red-600'>{validationErrors.contactPhone}</p>
            )}
          </div>
        </div>
      </Section>

      <Section title='Registered Office'>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          {[
            { key: 'addressLine1', label: 'Address Line 1' },
            { key: 'addressLine2', label: 'Address Line 2' },
            { key: 'town', label: 'Town / City' },
            { key: 'county', label: 'County' },
            { key: 'postCode', label: 'Postcode' },
            { key: 'country', label: 'Country' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className='mb-2 block text-sm font-medium text-gray-900'>{label}</label>
              <input
                type='text'
                value={companyInfo.registeredOffice[key as keyof typeof companyInfo.registeredOffice]}
                onChange={(e) => updateOffice(key, e.target.value)}
                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
              />
            </div>
          ))}
        </div>
      </Section>
    </div>
  )
}
